/**
 * Coalesces high-frequency JOB status events for a single consumer.
 * State consumers may update their latest snapshot immediately; callbacks are
 * flushed once per key/window so status ticks do not become desk chatter.
 */

import { deskGovernanceAuditLog, makeGovernanceMetadata } from '@/app/api/chat/audit/desk-governance-audit'

export type JobEventKey = `${string}:${string}`

export type CoalescedJobEvent<T> = {
  key: JobEventKey
  event: T
  suppressed: number
  stateHash: string
}

type Pending<T> = {
  event: T
  suppressed: number
  timer: ReturnType<typeof setTimeout>
}

export class JobEventCoalescer<T> {
  private readonly pending = new Map<JobEventKey, Pending<T>>()

  public constructor(
    private readonly windowMs = 30_000,
    private readonly onFlush: (event: CoalescedJobEvent<T>) => void
  ) {}

  public push(key: JobEventKey, event: T): boolean {
    if (!isSafeJobEvent(key, event)) return false
    const prior = this.pending.get(key)
    if (prior) {
      prior.event = event
      prior.suppressed += 1
      return true
    }
    const timer = setTimeout(() => this.flush(key), this.windowMs)
    this.pending.set(key, {
      event,
      suppressed: 0,
      timer,
    })
    return true
  }

  public flush(key: JobEventKey): void {
    const pending = this.pending.get(key)
    if (!pending) return
    clearTimeout(pending.timer)
    this.pending.delete(key)
    const stateHash = jobStateHash(pending.event)
    const decisionId = `${key}:${stateHash}`
    deskGovernanceAuditLog.append({
      id: `job-state-${decisionId}`,
      kind: 'job_state',
      agent: 'JOB',
      decisionId,
      stateHash,
      metadata: makeGovernanceMetadata({
        agent: 'JOB', capability: 'observe', mutationAuthority: 'none', stateHash,
        economic: { riskTier: 'balanced', exposureDelta: 0, entitlementTier: 'runtime', costImpact: 0 },
        decisionSeed: { key, stateHash }, lineage: ['research'],
      }),
      details: { key, suppressed: pending.suppressed },
      occurredAt: new Date().toISOString(),
    })
    this.onFlush({ key, event: pending.event, suppressed: pending.suppressed, stateHash })
  }

  public flushAll(): void {
    for (const key of this.pending.keys()) this.flush(key)
  }

  public clear(): void {
    for (const pending of this.pending.values()) clearTimeout(pending.timer)
    this.pending.clear()
  }
}

/** JOB may publish state, fills, or errors; control/advisory chatter is not a state event. */
export function isSafeJobEvent(key: JobEventKey, event: unknown): boolean {
  const kind = key.split(':').slice(1).join(':').toLowerCase()
  if (/(control|advisory|commentary|chat|instruction)/.test(kind)) {
    recordRejectedJobEvent(key, event, 'event_kind')
    return false
  }
  if (!event || typeof event !== 'object') return true
  const record = event as Record<string, unknown>
  const eventKind = String(record.type ?? record.kind ?? '').toLowerCase()
  const safe = !/(control_request|control|advisory|commentary|instruction)/.test(eventKind)
  if (!safe) recordRejectedJobEvent(key, event, 'event_payload')
  return safe
}

function recordRejectedJobEvent(key: JobEventKey, event: unknown, reason: string): void {
  const stateHash = jobStateHash(event)
  const decisionId = `rejected:${key}:${stateHash}`
  deskGovernanceAuditLog.append({
    id: `verifier-${decisionId}`,
    kind: 'verifier_failure',
    agent: 'JOB', decisionId, stateHash,
    metadata: makeGovernanceMetadata({
      agent: 'JOB', capability: 'validate', mutationAuthority: 'none', stateHash,
      economic: { riskTier: 'balanced', exposureDelta: 0, entitlementTier: 'runtime', costImpact: 0 },
      decisionSeed: { key, stateHash, reason }, lineage: ['research', 'validate'],
    }),
    details: { key, reason }, occurredAt: new Date().toISOString(),
  })
}

export function jobStateHash(event: unknown): string {
  const raw = stableSerialize(event)
  let hash = 2166136261
  for (let i = 0; i < raw.length; i += 1) {
    hash ^= raw.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}
